import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WordList,
  WordResult,
  CustomWordlistData,
  getWordDistances,
  applyFilter,
  fetchWordList,
  fetchWordListPreserveCase,
  customWordlistToWordList,
  loadCustomWordlist,
} from './wordfinderUtils';
import CustomWordlistDialog from './CustomWordlistDialog';
import './Wordfinder.less';

const Wordfinder: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [words41k, setWords41k] = useState<WordList | null>(null);
  const [words172k, setWords172k] = useState<WordList | null>(null);
  const [words380k, setWords380k] = useState<WordList | null>(null);
  const [broda, setBroda] = useState<WordList | null>(null);
  const [currentWordList, setCurrentWordList] = useState<WordList>([]);
  const [wordlistSize, setWordlistSize] = useState('41k');

  const [inputWord, setInputWord] = useState('');
  const [minLength, setMinLength] = useState('');
  const [maxLength, setMaxLength] = useState('');
  const [minDistance, setMinDistance] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const [filter, setFilter] = useState('all');

  const [results, setResults] = useState<WordResult[]>([]);
  const [loading, setLoading] = useState(false);

  // Custom wordlist state
  const [customWordlist, setCustomWordlist] = useState<CustomWordlistData | null>(null);
  const [minQuality, setMinQuality] = useState<number | undefined>(undefined);
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  // Fetch word lists on component mount
  useEffect(() => {
    fetchWordList('https://raw.githubusercontent.com/julianeden/english-words/refs/heads/master/40k.txt')
      .then(words => {
        setWords41k(words);
        setCurrentWordList(words);
      })
      .catch(error => console.error('Error fetching 41k word list:', error));

    fetchWordList('https://raw.githubusercontent.com/julianeden/english-words/master/172k.txt')
      .then(words => setWords172k(words))
      .catch(error => console.error('Error fetching 172k word list:', error));

    fetchWordListPreserveCase('https://raw.githubusercontent.com/julianeden/english-words/master/words_alpha.txt')
      .then(words => setWords380k(words))
      .catch(error => console.error('Error fetching 380k word list:', error));

    fetchWordList('https://raw.githubusercontent.com/julianeden/english-words/master/Broda-trimmed.txt')
      .then(words => setBroda(words))
      .catch(error => console.error('Error fetching Broda word list:', error));

    // Load custom wordlist from storage if it exists
    loadCustomWordlist()
      .then(data => {
        if (data) {
          setCustomWordlist(data);
          setWordlistSize('custom');
        }
      })
      .catch(error => console.error('Error loading custom wordlist:', error));
  }, []);

  // Update current word list when selection changes
  useEffect(() => {
    switch (wordlistSize) {
      case '41k':
        if (words41k) setCurrentWordList(words41k);
        break;
      case '172k':
        if (words172k) setCurrentWordList(words172k);
        break;
      case '380k':
        if (words380k) setCurrentWordList(words380k);
        break;
      case 'broda':
        if (broda) setCurrentWordList(broda);
        break;
      case 'custom':
        if (customWordlist) {
          setCurrentWordList(customWordlistToWordList(customWordlist, minQuality));
        }
        break;
    }
  }, [wordlistSize, words41k, words172k, words380k, broda, customWordlist, minQuality]);

  const handleWordlistUploaded = (wordlist: CustomWordlistData) => {
    setCustomWordlist(wordlist);
    setWordlistSize('custom');
    setMinQuality(undefined);
    setShowCustomDialog(false);
  };

  const handleWordlistCleared = () => {
    setCustomWordlist(null);
    setWordlistSize('41k');
    setMinQuality(undefined);
    setShowCustomDialog(false);
  };

  const performSearch = (word: string) => {
    if (!word) return;

    setLoading(true);
    setInputWord(word);

    // Update URL with search parameters
    const params: Record<string, string> = { word };
    if (minLength) params.minLength = minLength;
    if (maxLength) params.maxLength = maxLength;
    if (minDistance) params.minDistance = minDistance;
    if (maxDistance) params.maxDistance = maxDistance;
    if (wordlistSize !== '41k') params.wordlistSize = wordlistSize;
    if (filter !== 'all') params.filter = filter;
    setSearchParams(params);

    // Use setTimeout to allow UI to update before heavy computation
    setTimeout(() => {
      const minLen = minLength ? parseInt(minLength) : 1;
      const maxLen = maxLength ? parseInt(maxLength) : 9000;
      const minDist = minDistance ? parseInt(minDistance) : 1;
      const maxDist = maxDistance ? parseInt(maxDistance) : 2;

      let searchResults = getWordDistances(word, currentWordList, minLen, maxLen, minDist, maxDist);
      searchResults = applyFilter(searchResults, filter);

      setResults(searchResults);
      setLoading(false);
    }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(inputWord.toLowerCase());
  };

  const handleResultClick = (word: string) => {
    window.scrollTo(0, 0);
    performSearch(word);
  };

  const handlePreviousSearch = () => {
    window.history.back();
  };

  // Load search params from URL on mount and when URL changes (browser back/forward)
  useEffect(() => {
    const word = searchParams.get('word');
    if (!word) return;

    setInputWord(word);
    setMinLength(searchParams.get('minLength') || '');
    setMaxLength(searchParams.get('maxLength') || '');
    setMinDistance(searchParams.get('minDistance') || '');
    setMaxDistance(searchParams.get('maxDistance') || '');
    setWordlistSize(searchParams.get('wordlistSize') || '41k');
    setFilter(searchParams.get('filter') || 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Perform search when URL params change and wordlist is ready
  useEffect(() => {
    const word = searchParams.get('word');
    if (!word || currentWordList.length === 0) return;

    const minLen = searchParams.get('minLength') ? parseInt(searchParams.get('minLength')!) : 1;
    const maxLen = searchParams.get('maxLength') ? parseInt(searchParams.get('maxLength')!) : 9000;
    const minDist = searchParams.get('minDistance') ? parseInt(searchParams.get('minDistance')!) : 1;
    const maxDist = searchParams.get('maxDistance') ? parseInt(searchParams.get('maxDistance')!) : 2;
    const filterParam = searchParams.get('filter') || 'all';

    setLoading(true);
    setTimeout(() => {
      let searchResults = getWordDistances(word, currentWordList, minLen, maxLen, minDist, maxDist);
      searchResults = applyFilter(searchResults, filterParam);

      setResults(searchResults);
      setLoading(false);
    }, 0);
  }, [searchParams, currentWordList]);

  return (
    <div className="wordfinderContainer">
      <div className="wordfinderFormContainer">
        <form onSubmit={handleSubmit}>
          <div className="wordfinderFormGroup">
            <label htmlFor="inputWord">Input Word:</label>
            <input
              type="text"
              id="inputWord"
              value={inputWord}
              onChange={(e) => setInputWord(e.target.value)}
              autoComplete="off"
              required
            />
          </div>
          <div className="wordfinderFormGroup">
            <label htmlFor="minLength">Min Length:</label>
            <input
              type="number"
              id="minLength"
              value={minLength}
              onChange={(e) => setMinLength(e.target.value)}
              placeholder="1"
            />
          </div>
          <div className="wordfinderFormGroup">
            <label htmlFor="maxLength">Max Length:</label>
            <input
              type="number"
              id="maxLength"
              value={maxLength}
              onChange={(e) => setMaxLength(e.target.value)}
              placeholder="No max"
            />
          </div>
          <div className="wordfinderFormGroup">
            <label htmlFor="minDistance">Min Distance:</label>
            <input
              type="number"
              id="minDistance"
              value={minDistance}
              onChange={(e) => setMinDistance(e.target.value)}
              placeholder="1"
            />
          </div>
          <div className="wordfinderFormGroup">
            <label htmlFor="maxDistance">Max Distance:</label>
            <input
              type="number"
              id="maxDistance"
              value={maxDistance}
              onChange={(e) => setMaxDistance(e.target.value)}
              placeholder="2"
            />
          </div>
          <div className="wordfinderFormGroup">
            <label htmlFor="wordlistSize">Use wordlist with size:</label>
            <select
              id="wordlistSize"
              value={wordlistSize}
              onChange={(e) => setWordlistSize(e.target.value)}
            >
              <option value="41k">41k</option>
              <option value="172k">172k</option>
              <option value="380k">380k</option>
              <option value="broda">Broda (280k, includes phrases + names)</option>
              {customWordlist && (
                <option value="custom">
                  Custom ({customWordlist.wordCount.toLocaleString()} words)
                </option>
              )}
            </select>
          </div>
          <div className="wordfinderFormGroup">
            <button
              type="button"
              className="wordfinderCustomButton"
              onClick={() => setShowCustomDialog(true)}
            >
              {customWordlist ? 'Change custom wordlist' : 'Use custom wordlist'}
            </button>
          </div>
          {customWordlist?.hasQualityRatings && wordlistSize === 'custom' && (
            <div className="wordfinderFormGroup">
              <label htmlFor="minQuality">Minimum word quality:</label>
              <select
                id="minQuality"
                value={minQuality ?? ''}
                onChange={(e) => setMinQuality(e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">All qualities</option>
                {customWordlist.qualityLevels.map(level => (
                  <option key={level} value={level}>
                    {level}+
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="wordfinderFilterOptions">
            <div>
              <input
                type="radio"
                id="includeAll"
                name="filter"
                value="all"
                checked={filter === 'all'}
                onChange={(e) => setFilter(e.target.value)}
              />
              <label htmlFor="includeAll">Include all results</label>
            </div>
            <div>
              <input
                type="radio"
                id="onlyPlusMinus"
                name="filter"
                value="plusMinus"
                checked={filter === 'plusMinus'}
                onChange={(e) => setFilter(e.target.value)}
              />
              <label htmlFor="onlyPlusMinus">Only +/-, no ~</label>
            </div>
            <div>
              <input
                type="radio"
                id="onlyTilde"
                name="filter"
                value="tilde"
                checked={filter === 'tilde'}
                onChange={(e) => setFilter(e.target.value)}
              />
              <label htmlFor="onlyTilde">Only ~, no +/-</label>
            </div>
          </div>
          <br/>
          <button className="wordfinderSubmitButton" type="submit">Find Words</button>
          <button className="wordfinderPreviousButton" type="button" onClick={handlePreviousSearch}>
            Previous search
          </button>
        </form>
      </div>
      <div className="wordfinderResultsContainer">
        {loading ? (
          <p>Searching...</p>
        ) : results.length > 0 ? (
          <>
            <p>{results.length} matches found</p>
            {results.map((result, index) => {
              // Calculate net morph size for current and previous result
              const calculateNetMorphSize = (distance: string): number => {
                let netSize = 0;
                const tildeMatch = distance.match(/~(\d+)/);
                const plusMatch = distance.match(/\+(\d+)/);
                const minusMatch = distance.match(/-(\d+)/);
                if (tildeMatch) netSize += parseInt(tildeMatch[1]);
                if (plusMatch) netSize += parseInt(plusMatch[1]);
                if (minusMatch) netSize += parseInt(minusMatch[1]);
                return netSize;
              };

              const currentNetSize = calculateNetMorphSize(result.distance);
              const prevNetSize = index > 0 ? calculateNetMorphSize(results[index - 1].distance) : -1;
              const showDivider = index > 0 && currentNetSize !== prevNetSize;

              return (
                <React.Fragment key={index}>
                  {showDivider && <hr className="morphSizeDivider" />}
                  <p className="wordfinderSearchResult">
                    <span className="matchText" onClick={() => handleResultClick(result.word)}>
                      {result.word}
                    </span>
                    <span className="matchDistance">{result.distance}</span>
                  </p>
                </React.Fragment>
              );
            })}
          </>
        ) : inputWord && !loading ? (
          <p>No matches found</p>
        ) : (
          <p>Enter a word and click "Find Words" to search</p>
        )}
      </div>

      {/* Custom Wordlist Upload Dialog */}
      {showCustomDialog && (
        <CustomWordlistDialog
          existingWordlist={customWordlist}
          onClose={() => setShowCustomDialog(false)}
          onWordlistUploaded={handleWordlistUploaded}
          onWordlistCleared={handleWordlistCleared}
        />
      )}
    </div>
  );
};

export default Wordfinder;
